import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

function Light({ ok, label, testid }) {
  return (
    <div className="flex items-center gap-3 border border-sage-border p-3" data-testid={testid}>
      <div
        className={`h-3 w-3 ${ok
          ? "bg-sage-accent shadow-[0_0_10px_rgba(225,29,72,0.7)]"
          : "bg-sage-border"}`}
      />
      <div className="text-xs uppercase tracking-[0.1em] text-sage-muted">{label}</div>
      <div className={`ml-auto text-[10px] uppercase tracking-[0.15em] ${ok ? "text-sage-accent" : "text-sage-warn"}`}>
        {ok ? "READY" : "PENDING"}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([api.admin.status(), api.admin.witnessLog()]);
      setStatus(s);
      setLog(l.log || []);
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 15000);
    return () => clearInterval(iv);
  }, []);

  const flip = async (fn, value, prompt) => {
    const reason = window.prompt(prompt) || "";
    if (!reason) return;
    setBusy(true);
    try {
      await fn(value, reason);
      toast.success("Toggle recorded.");
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const lights = status?.lights || {};
  const lightsSpec = [
    ["key_valid", "Anthropic key", "light-key"],
    ["db_connected", "DB connected", "light-db"],
    ["master_token_set", "Master token", "light-master"],
    ["ceiling_set", "Daily ceiling", "light-cap"],
    ["warden_responding", "Warden responding", "light-warden"],
    ["glass_laws_pass", "Glass Laws present", "light-laws"],
  ];

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-8">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-4">Admin · master-only</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text mb-6">
          Deploy Readiness
        </h1>

        {/* Six lights */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-sage-border" data-testid="lights-grid">
          {lightsSpec.map(([key, label, testid]) => (
            <Light key={key} ok={!!lights[key]} label={label} testid={testid} />
          ))}
        </div>

        {/* Launch */}
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            data-testid="launch-btn"
            disabled={busy || !status?.all_green}
            onClick={() => flip(api.admin.launch, !status?.launched, "Reason for launch toggle:")}
            className={`px-6 py-3 uppercase tracking-[0.1em] text-xs border transition-colors duration-150 ${
              status?.all_green
                ? "border-sage-accent text-sage-accent hover:bg-sage-accent hover:text-sage-bg"
                : "border-sage-border text-sage-faint cursor-not-allowed"
            }`}
          >
            {status?.launched ? "◼︎ Landing LIVE — click to hide" : "▶ Launch landing page"}
          </button>
          <button
            data-testid="kill-btn"
            disabled={busy}
            onClick={() => flip(api.admin.killSwitch, !status?.kill_switch, "Reason for kill-switch toggle:")}
            className={`px-6 py-3 uppercase tracking-[0.1em] text-xs border transition-colors duration-150 ${
              status?.kill_switch
                ? "border-sage-err text-sage-err hover:bg-sage-err hover:text-sage-bg"
                : "border-sage-border text-sage-muted hover:border-sage-err hover:text-sage-err"
            }`}
          >
            {status?.kill_switch ? "◼︎ Kill switch ON — click to release" : "☒ Kill switch"}
          </button>
          <button
            data-testid="freeze-btn"
            disabled={busy}
            onClick={() => flip(api.admin.deployFreeze, !status?.deploy_freeze, "Reason for deploy-freeze toggle:")}
            className="px-6 py-3 uppercase tracking-[0.1em] text-xs border border-sage-border text-sage-muted hover:border-sage-warn hover:text-sage-warn transition-colors duration-150"
          >
            {status?.deploy_freeze ? "◼︎ Deploys frozen" : "⚠ Freeze deploys"}
          </button>
          <button
            data-testid="fund-gate-toggle"
            disabled={busy}
            onClick={async () => {
              const flags = await api.flags();
              flip(api.admin.fundTheGate, !flags.fund_the_gate_visible, "Reason for Fund-the-Gate visibility toggle:");
            }}
            className="px-6 py-3 uppercase tracking-[0.1em] text-xs border border-sage-border text-sage-muted hover:border-sage-accent hover:text-sage-accent transition-colors duration-150"
          >
            Fund-the-Gate button
          </button>
          <button
            data-testid="cold-read-run"
            disabled={busy}
            onClick={async () => {
              if (!window.confirm("Run Cold Read now? Costs ~$0.05 in Anthropic tokens and takes ~8 minutes.")) return;
              setBusy(true);
              try {
                toast.info("Cold Read started — check back in ~8 min.");
                await api.coldRead.run("manual admin trigger");
                toast.success("Cold Read complete.");
                await refresh();
              } catch (e) {
                toast.error(e.message);
              } finally {
                setBusy(false);
              }
            }}
            className="px-6 py-3 uppercase tracking-[0.1em] text-xs border border-sage-accent text-sage-accent hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
          >
            ▶ Run Cold Read now
          </button>
        </div>

        {/* Spend */}
        {status && (
          <div className="mt-8 grid md:grid-cols-3 border border-sage-border divide-y md:divide-y-0 md:divide-x divide-sage-border">
            <div className="p-4">
              <div className="text-[10px] uppercase text-sage-faint">Spent today</div>
              <div className="text-2xl font-bold tabular-nums text-sage-accent">${status.spent_today_usd.toFixed(6)}</div>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase text-sage-faint">Daily ceiling</div>
              <div className="text-2xl font-bold tabular-nums text-sage-text">${status.daily_cap_usd.toFixed(2)}</div>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase text-sage-faint">All green</div>
              <div className={`text-2xl font-bold ${status.all_green ? "text-sage-accent" : "text-sage-warn"}`}>
                {status.all_green ? "YES" : "NO"}
              </div>
            </div>
          </div>
        )}

        {/* Lore Visibility · master toggle — Founder's ruling 2026-07-31 */}
        <LoreVisibilityBlock />

        {/* Witness log */}
        <div className="mt-8 border border-sage-border" data-testid="witness-log">
          <div className="p-4 border-b border-sage-border text-xs uppercase tracking-[0.15em] text-sage-muted">
            Witness log · every admin action, timestamped
          </div>
          <div className="divide-y divide-sage-border max-h-96 overflow-y-auto">
            {log.length === 0 && <div className="p-6 text-sage-faint text-xs">No admin actions yet.</div>}
            {log.map((entry, i) => (
              <div key={i} className="p-3 text-xs grid grid-cols-4 gap-2">
                <div className="text-sage-faint tabular-nums">{entry.at}</div>
                <div className="text-sage-accent">{entry.action}</div>
                <div className="text-sage-muted col-span-2 truncate">{entry.reason}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── LoreVisibilityBlock ────────────────────────────────────────────────
// Founder's master switch (2026-07-31): controls whether the full Founder's
// Codex and the coffin-nails content surface on Sage's public rails.
// Default OFF (private); flips are witness-logged with a required reason.
function LoreVisibilityBlock() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const load = async () => {
    try {
      const d = await api.loreVisibility.get();
      setFlags({
        coffin_nails_public: !!d.coffin_nails_public,
        founders_codex_public: !!d.founders_codex_public,
      });
      setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
    } catch (e) {
      toast.error(`Lore visibility load failed · ${e.message}`);
    }
  };

  useEffect(() => { load(); }, []);

  const flip = async (key) => {
    if (!flags) return;
    const target = !flags[key];
    const label = key === "coffin_nails_public" ? "Coffin Nails" : "Founder's Codex";
    const reason = window.prompt(
      `Flip ${label} to ${target ? "PUBLIC" : "PRIVATE"} — one line reason (required):`,
    );
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      const d = await api.loreVisibility.set({ [key]: target }, reason.trim());
      setFlags({
        coffin_nails_public: !!d.coffin_nails_public,
        founders_codex_public: !!d.founders_codex_public,
      });
      setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
      toast.success(`${label} is now ${target ? "PUBLIC" : "PRIVATE"}.`);
    } catch (e) {
      toast.error(`Toggle failed · ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!flags) return null;

  const rows = [
    {
      k: "coffin_nails_public",
      label: "Coffin Nails on public rails",
      note: "Currently wired to: NOTHING on Sage (the § 08 One-Inch Nail rendering was the neighbour house-scope and was scrubbed 2026-07-31). Flag remains as forward-looking infrastructure for any future hand-picked Sage-side release; keep it PRIVATE until an actual Sage surface is wired.",
    },
    {
      k: "founders_codex_public",
      label: "Founder's Codex at /api/sage/founders-codex",
      note: "Public endpoint returns Parts I + II (Part III always sealed master-side) when PUBLIC; returns a sealed placeholder when PRIVATE. Admin route /api/sage/admin/founders-codex is unaffected — always master-only, always full.",
    },
  ];

  return (
    <div className="mt-8 border border-sage-border" data-testid="lore-visibility-block">
      <div className="p-4 border-b border-sage-border">
        <div className="text-xs uppercase tracking-[0.15em] text-sage-muted">
          Lore visibility · master switch
        </div>
        <div className="text-[10px] text-sage-faint mt-2 sage-prose max-w-3xl">
          {"Default OFF (private) per Founder\u2019s ruling 2026-07-31. When PRIVATE, the full ledger stays in the vault and public rails return a graceful sealed placeholder; when PUBLIC, the ledger surfaces on the rails wired to that flag. The Founder hand-picks releases; per-item allowlist can arrive in a later slice on his word."}
        </div>
      </div>
      <div className="p-4 space-y-3">
        {rows.map((r) => {
          const on = flags[r.k];
          return (
            <div
              key={r.k}
              className="flex items-center gap-4 border border-sage-border p-4"
              data-testid={`lore-vis-row-${r.k}`}
            >
              <div className="flex-1">
                <div className="text-sm uppercase tracking-[0.15em] text-sage-text">{r.label}</div>
                <div className="text-[10px] text-sage-faint mt-1 sage-prose max-w-xl">{r.note}</div>
              </div>
              <div
                className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${
                  on ? "border-sage-accent text-sage-accent" : "border-sage-border text-sage-muted"
                }`}
                data-testid={`lore-vis-state-${r.k}`}
              >
                {on ? "PUBLIC" : "PRIVATE"}
              </div>
              <button
                disabled={busy}
                onClick={() => flip(r.k)}
                data-testid={`lore-vis-flip-${r.k}`}
                className="px-4 py-2 border border-sage-border text-sage-muted text-[10px] uppercase tracking-[0.15em] hover:border-sage-accent hover:text-sage-accent transition-colors duration-150 disabled:opacity-40"
              >
                Flip
              </button>
            </div>
          );
        })}
        {meta.updated_at && (
          <div className="text-[10px] text-sage-faint pt-2">
            Last flipped: <span className="tabular-nums">{meta.updated_at}</span> by {meta.updated_by || "master"}
          </div>
        )}
      </div>
    </div>
  );
}
